import React, { useState, useEffect } from "react";
import { CalendarCheck, Check, X, History } from "lucide-react";
import { getViewings, updateViewingStatus, submitViewingFeedback } from "../../api";
import { useToast } from "../../hooks/use-toast";

const sColor = { accepted: "bg-green-100 text-green-700", declined: "bg-red-100 text-red-600", completed: "bg-blue-100 text-blue-700", requested: "bg-amber-100 text-amber-700" };

const AdminViewings = () => {
  const { toast } = useToast();
  const [viewings, setViewings] = useState([]);
  const [tab, setTab] = useState("active");
  const refresh = () => getViewings().then(setViewings).catch(() => setViewings([]));
  useEffect(() => { refresh(); }, []);

  const setStatus = async (id, status) => {
    try { await updateViewingStatus(id, status); setViewings((vs) => vs.map((v) => v.id === id ? { ...v, status } : v)); toast({ title: `Viewing ${status}` }); }
    catch { toast({ title: "Update failed" }); }
  };
  const giveFeedback = async (id, interest) => {
    try { const u = await submitViewingFeedback(id, { interest }); setViewings((vs) => vs.map((v) => v.id === id ? u : v)); toast({ title: `Feedback saved — ${interest} interest` }); }
    catch { toast({ title: "Couldn't save feedback" }); }
  };

  const active = viewings.filter((v) => ["requested", "accepted"].includes(v.status));
  const past = viewings.filter((v) => ["completed", "declined"].includes(v.status));
  const rows = tab === "active" ? active : past;

  return (
    <div className="bg-white rounded-2xl border border-[#f0ece5] p-6" data-testid="admin-viewings">
      <div className="inline-flex rounded-full bg-[#faf7f2] border border-[#f0ece5] p-1 mb-5">
        <button onClick={() => setTab("active")} data-testid="viewings-tab-active" className={`px-5 py-2 rounded-full text-[13px] font-500 inline-flex items-center gap-2 transition-colors ${tab === "active" ? "bg-[#b8935f] text-white" : "text-[#6a6a6a]"}`}><CalendarCheck size={15} /> Active & Upcoming ({active.length})</button>
        <button onClick={() => setTab("past")} data-testid="viewings-tab-past" className={`px-5 py-2 rounded-full text-[13px] font-500 inline-flex items-center gap-2 transition-colors ${tab === "past" ? "bg-[#b8935f] text-white" : "text-[#6a6a6a]"}`}><History size={15} /> Past ({past.length})</button>
      </div>

      {rows.length === 0 ? <p className="p-10 text-center text-[#8a8a8a]">No {tab === "active" ? "active" : "past"} viewings.</p> : (
        <div className="space-y-2">
          {rows.map((v) => (
            <div key={v.id} className="px-4 py-3.5 rounded-xl bg-[#faf7f2] border border-[#f0ece5]" data-testid="viewing-row">
              <div className="flex items-center justify-between gap-3">
                <div><p className="text-[15px] font-500 text-[#2b2b2b]">{v.name}</p><p className="text-[13px] text-[#8a8a8a]">{v.email} · {v.propertyTitle || v.propertyId}</p></div>
                <div className="flex items-center gap-2">
                  <span className={`text-[11px] px-2.5 py-1 rounded-full capitalize ${sColor[v.status]}`}>{v.status}</span>
                  {v.status === "requested" && (
                    <>
                      <button onClick={() => setStatus(v.id, "accepted")} className="w-8 h-8 rounded-lg border border-[#e2d8c8] flex items-center justify-center text-green-600 hover:border-green-400"><Check size={15} /></button>
                      <button onClick={() => setStatus(v.id, "declined")} className="w-8 h-8 rounded-lg border border-[#e2d8c8] flex items-center justify-center text-red-500 hover:border-red-400"><X size={15} /></button>
                    </>
                  )}
                  {v.status === "accepted" && <button onClick={() => setStatus(v.id, "completed")} data-testid="viewing-complete-btn" className="px-3 py-1.5 rounded-lg border border-[#e2d8c8] text-[12px] text-[#6a6a6a] hover:border-[#b8935f] hover:text-[#b8935f]">Mark completed</button>}
                </div>
              </div>
              {v.status !== "requested" && v.status !== "declined" && (
                <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="viewing-feedback">
                  {v.feedback ? (
                    <span className="text-[12px] text-[#6a6a6a]">Feedback: <span className="capitalize font-500 text-[#b8935f]">{v.feedback.interest} interest</span>{v.feedback.comment ? ` — ${v.feedback.comment}` : ""}</span>
                  ) : (
                    <>
                      <span className="text-[12px] text-[#a0a0a0]">Log feedback:</span>
                      {["high", "medium", "low"].map((lvl) => (
                        <button key={lvl} onClick={() => giveFeedback(v.id, lvl)} data-testid={`feedback-${lvl}`} className="text-[12px] capitalize px-3 py-1.5 rounded-full border border-[#e2d8c8] text-[#6a6a6a] hover:border-[#b8935f] hover:text-[#b8935f]">{lvl}</button>
                      ))}
                    </>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminViewings;
